import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { WorkspaceProvider, useWorkspace } from "./contexts/WorkspaceContext";
import Shop from "./pages/Shop";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import DataCollection from "./pages/DataCollection";
import DataDeletion from "./pages/DataDeletion";
import { runMigrations } from "./lib/storage";
import { startWebhookListener, stopWebhookListener } from "./services/webhookProcessor";
import { triggerService } from "./services/triggerService";
import { syncLatestIntegrationsToDb } from "./services/dbMigrationService";
import { syncFromDb, setupRealtimeSync, resetDbSync } from "./services/dbSyncService";
import { reloadAutomations } from "./services/automationDbService";
import { Loader2, ShieldAlert } from "lucide-react";

const Layout = lazy(() => import("./components/Layout"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="animate-spin text-primary" size={32} />
  </div>
);

function ProtectedApp() {
  const { accountStatus, workspaceId } = useWorkspace();

  useEffect(() => {
    // CRITICAL: do not start sync until we have a real, resolved workspace_id.
    // Otherwise getCurrentWorkspaceId() may still return a stale 'default' (or a
    // previous user's workspace) and we'd hydrate the inbox with another
    // account's conversations until the next manual refresh.
    if (!workspaceId || workspaceId === 'default') return;

    let cancelled = false;
    // Reset any previously cached data (from another workspace / previous session)
    // before we start the fresh sync for the now-resolved workspace.
    resetDbSync();

    runMigrations();
    triggerService.syncFromDb();
    void syncLatestIntegrationsToDb();

    void (async () => {
      await syncFromDb();
      // Reload automations now that the workspace is resolved — initial
      // import-time load happened before the workspace was known and would
      // have returned an empty set due to RLS filtering.
      await reloadAutomations();
      if (cancelled) return;
      console.log('[App] DB sync complete for workspace', workspaceId, '— starting realtime + webhook listener');
      setupRealtimeSync();
      startWebhookListener();
    })();

    return () => {
      cancelled = true;
      stopWebhookListener();
      resetDbSync();
    };
  }, [workspaceId]);

  // Account suspended/blocked
  if (accountStatus === 'suspended' || accountStatus === 'blocked') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md p-8">
          <ShieldAlert className="mx-auto mb-4 text-destructive" size={48} />
          <h1 className="text-2xl font-bold mb-2">
            Account {accountStatus === 'suspended' ? 'Suspended' : 'Blocked'}
          </h1>
          <p className="text-muted-foreground">
            {accountStatus === 'suspended'
              ? 'Your account has been temporarily suspended. Please contact the platform administrator.'
              : 'Your account has been blocked. Please contact support for assistance.'}
          </p>
        </div>
      </div>
    );
  }

  return <Layout />;
}

function AppRoutes() {
  const { user, loading: authLoading } = useAuth();
  const { isSuperAdmin, isLoading: wsLoading } = useWorkspace();

  if (authLoading || (user && wsLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="animate-spin text-primary mx-auto mb-3" size={32} />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/shop" element={<Shop />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/privacy-policy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/terms-of-service" element={<Terms />} />
        <Route path="/data-collection" element={<DataCollection />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/signup" element={user ? <Navigate to="/dashboard" replace /> : <Signup />} />
        <Route path="/admin/*" element={
          user && isSuperAdmin ? <AdminDashboard /> : <Navigate to="/dashboard" replace />
        } />
        <Route path="/dashboard/*" element={user ? <ProtectedApp /> : <Navigate to="/login" replace />} />
        <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <Navigate to="/shop" replace />} />
        <Route path="/*" element={user ? <ProtectedApp /> : <Navigate to="/shop" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WorkspaceProvider>
          <AppRoutes />
        </WorkspaceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
